import React, { useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

const YearSelector = ({ value, onChange, min = 2000, max = 2100, className = '', size = 'default' }) => {
  const [isChanging, setIsChanging] = useState(false);
  const isCompact = size === 'compact';

  const buttonPaddingClass = isCompact ? 'p-0.5 sm:p-0.5' : 'p-1';
  const buttonIconClass = isCompact ? 'w-3 h-3 sm:w-3 sm:h-3' : 'w-3 h-3 sm:w-4 sm:h-4';
  const inputClass = isCompact
    ? 'bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] border border-[#E5E7EB] dark:border-[#374151] rounded-lg px-2 py-1 sm:px-2.5 sm:py-1.5 text-xs sm:text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#3B82F6] dark:focus:ring-[#60A5FA] focus:border-[#3B82F6] dark:focus:border-[#60A5FA] transition-all duration-200 shadow-sm hover:shadow-md min-w-[62px] sm:min-w-[80px] text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none h-[30px] sm:h-[38px]'
    : 'bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] border border-[#E5E7EB] dark:border-[#374151] rounded-md px-2 py-1.5 sm:px-3 sm:py-2.5 text-xs sm:text-base font-semibold focus:outline-none focus:ring-2 focus:ring-[#3B82F6] dark:focus:ring-[#60A5FA] focus:border-[#3B82F6] dark:focus:border-[#60A5FA] transition-all duration-200 shadow-sm hover:shadow-md min-w-[60px] sm:min-w-[100px] text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none h-[32px] sm:h-[56px]';

  const handleIncrement = () => {
    if (value < max) {
      setIsChanging(true);
      onChange(value + 1);
      setTimeout(() => setIsChanging(false), 300);
    }
  };

  const handleDecrement = () => {
    if (value > min) {
      setIsChanging(true);
      onChange(value - 1);
      setTimeout(() => setIsChanging(false), 300);
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 1 : -1;
    const newValue = value + delta;
    if (newValue >= min && newValue <= max) {
      setIsChanging(true);
      onChange(newValue);
      setTimeout(() => setIsChanging(false), 300);
    }
  };

  const handleInputChange = (e) => {
    const val = Number(e.target.value);
    if (val >= min && val <= max) {
      setIsChanging(true);
      onChange(val);
      setTimeout(() => setIsChanging(false), 300);
    }
  };

  return (
    <div className={`relative flex items-center ${className}`}>
      {/* Custom Up/Down Buttons */}
      <div className="flex flex-col mr-1 sm:mr-2">
        <button
          onClick={handleIncrement}
          disabled={value >= max}
          className={`${buttonPaddingClass} ${isCompact ? 'rounded-t-lg' : 'rounded-t-md'} bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] border-b-0 hover:bg-[#F3F4F6] dark:hover:bg-[#374151] disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 group`}
          type="button"
        >
          <ChevronUpIcon className={`${buttonIconClass} text-[#6B7280] dark:text-[#9CA3AF] group-hover:text-[#3B82F6] dark:group-hover:text-[#60A5FA] transition-colors`} />
        </button>
        <button
          onClick={handleDecrement}
          disabled={value <= min}
          className={`${buttonPaddingClass} ${isCompact ? 'rounded-b-lg' : 'rounded-b-md'} bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] hover:bg-[#F3F4F6] dark:hover:bg-[#374151] disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 group`}
          type="button"
        >
          <ChevronDownIcon className={`${buttonIconClass} text-[#6B7280] dark:text-[#9CA3AF] group-hover:text-[#3B82F6] dark:group-hover:text-[#60A5FA] transition-colors`} />
        </button>
      </div>

      {/* Year Input */}
      <div className="relative">
        <motion.input
          type="number"
          value={value}
          onChange={handleInputChange}
          onWheel={handleWheel}
          min={min}
          max={max}
          animate={isChanging ? { scale: [1, 1.05, 1] } : {}}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className={inputClass}
        />
        {/* Subtle glow effect on focus */}
        <motion.div
          className="absolute inset-0 rounded-md pointer-events-none"
          animate={isChanging ? { 
            boxShadow: [
              '0 0 0px transparent',
              '0 0 8px var(--theme-accent-soft)',
              '0 0 0px transparent'
            ]
          } : {}}
          transition={{ duration: 0.3 }}
        />
      </div>
    </div>
  );
};

export default YearSelector;

